<template>
    <VDropdown
        v-if="!hideOnEmptyActions || hasValidActions"
        auto-hide-on-mousedown
        class="kliniken-dropdown-menu"
        placement="bottom-start"
        boundary="#app"
    >
        <KButton
            :disabled="!hasValidActions"
            class="toggle"
            type="button"
            size="icon"
            variant="contour"
            @click="onShow"
        >
            <img src="@/assets/message.svg" />
        </KButton>
        <template #popper>
            <ul :class="['dropdown-menu', popperClass ?? '']">
                <li
                    v-for="(action, index) in dropdownActions"
                    v-show="action.show(item)"
                    :key="index"
                    v-close-popper
                    @click.prevent="onClick({ action, item })"
                >
                    <img v-if="action.icon" :src="action.icon" />
                    <span> {{ action.title }} </span>
                </li>
            </ul>
        </template>
    </VDropdown>
</template>

<script lang="ts">
    export default {
        name: "KlinikenDropdownMenu",
    };
</script>

<script setup lang="ts">
    import { Action } from "@/components/Table/types";
    import { useStore } from "@/store";
    import { computed, toRefs, watch } from "vue";
    import KButton from "@/components/Button.vue";

    const store = useStore();

    const props = withDefaults(
        defineProps<{
            dropdownActions?: Action[];
            item: { [key: string]: unknown };
            /**
             * Ska knapp och meny döljas om det inte finns några actions?
             */
            hideOnEmptyActions?: boolean;
            /**
             * Custom class som sätts på innehållet
             */
            popperClass?: string;
        }>(),
        {
            dropdownActions: () => [],
            hideOnEmptyActions: false,
        }
    );

    const emit = defineEmits<{
        (event: "action", payload: { action: string; item: unknown }): void;
        (event: "show", payload: { item: unknown }): void;
    }>();

    const { dropdownActions, item } = toRefs(props);

    const hasValidActions = computed(() => {
        return dropdownActions.value.some((action) => action.show(item.value));
    });

    const onShow = () => {
        emit("show", { item: item.value });
    };

    const currentPatientId = computed(() => store.getters["patientData/currentId"]);
    const onClick: (props: { action: Action; item: { [key: string]: unknown } }) => void = ({ action, item }) => {
        /**
         * De flesta patientbundna objekt vi behandlar i listor innehåller fältet patient som är ett id.
         * I de fall patient är själva patientobjektet behöver vi plocka ut id:t.
         */
        let patientId: string | { id: string } | null;
        if (typeof item.patient === "string") {
            patientId = item.patient;
        } else {
            patientId = (item.patient as { id: string })?.id;
        }

        /**
         * Vi kollar om item som skickas in inte innehåller patient och därför inte är patientbunden, eller att
         * aktiv patient överenstämmer med patienten i item. Då kan vi emitta action direkt utan att byta
         * aktiv patient
         */
        if (!patientId || currentPatientId.value === patientId) {
            if (action.name) {
                emit("action", { action: action.name, item });
            } else {
                action?.callback?.({ data: item });
            }

            return;
        }

        /**
         * Om aktiv patient inte överenstämmer med patient i item skapar vi en watcher som lyssnar på förändringar
         * på patientData/currentId i store. Det måste vara en watch, eftersom att ändring av patientData/currentId
         * inte sker direkt.
         * När patientData/currentId ändrats kommer watch att köras. Om nya värdet på patientData/currentId är samma som
         * det i item så kommer action emittas, annars tas watchern bara bort
         */
        const unwatch = watch(currentPatientId, (newValue) => {
            if (newValue === item.patient) {
                if (action.name) {
                    emit("action", { action: action.name, item });
                } else {
                    action?.callback?.({ data: item });
                }
            } else {
                unwatch();
            }
        });
    };
</script>

<style lang="scss" scoped>
    @import "@/style/_variables.scss";

    .kliniken-dropdown-menu {
        position: relative;

        .toggle {
            border: 1px solid $color-menu-button-border;
            width: 2rem;
            height: 1.5rem;
            padding: unset;
            padding: 0.4rem;
        }
    }

    .dropdown-menu {
        list-style: none;
        margin: 0;
        background-color: #ffffff;
        border: 1px solid $color-menu-border;
        border-radius: $border-radius;
        width: fit-content;
        max-width: 100%;
        padding: 1rem 0;

        li {
            cursor: default;
            white-space: nowrap;
            display: flex;
            padding: 17px 25px;

            &:hover {
                background-color: $color-menu-hover;
            }

            img {
                height: 22px;
                width: 22px;
                margin-right: 11px;
            }
        }
    }
</style>
